import { useModalStatusMessage } from '@/hooks';
import { useMutation, useSuspenseQuery } from '@apollo/client';
import UPDATE_DEVICE_COLLECTION_RELATIONS from '@features/collections/graphql/mutations/updateDeviceCollectionRelations';
import { CollectionDeviceValue, DeviceCollectionsAccessInput } from '@features/collectionsAccessInputs';
import GET_COLLECTIONS_FOR_DEVICE_ACCESS from '@features/collectionsAccessInputs/graphql/queries/getCollectionsForDeviceAccess';
import { msg, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Typography } from '@mui/material';
import { useEffect, type FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import GET_ALL_DEVICE_ACCESS_FOR_DEVICE from '../allAccessTab/graphql/queries/getAllDeviceAccessForDevice';
import { TabContent, TabProps } from '../common';
import GET_DEVICE_ORGANIZATION from './getDeviceOrganization';

type FormValues = {
  deviceId: string;
  organizationIds: number[];
  updatedRelations: CollectionDeviceValue[];
};

export const CollectionsAccessTab: FC<TabProps> = ({ deviceId, onClose, setIsDirty }) => {
  const { _ } = useLingui();
  const { openModalErrorMessage, openModalSuccessMessage } = useModalStatusMessage();

  const { data } = useSuspenseQuery(GET_DEVICE_ORGANIZATION, {
    variables: { deviceId },
  });
  const deviceOrganizationId = data.device.profile?.organization?.id;

  const methods = useForm<FormValues>({
    defaultValues: {
      deviceId,
      organizationIds: deviceOrganizationId != null ? [deviceOrganizationId] : [],
      updatedRelations: [],
    },
  });

  useEffect(() => setIsDirty(methods.formState.isDirty), [methods.formState.isDirty, setIsDirty]);

  const [updateRelations] = useMutation(UPDATE_DEVICE_COLLECTION_RELATIONS, {
    refetchQueries: [
      {
        query: GET_COLLECTIONS_FOR_DEVICE_ACCESS,
        variables: { organizationIds: methods.getValues('organizationIds'), deviceId },
      },
      // AMARU-609 (mirror AMARU-562): el tab "Todos los accesos" agrega este mismo dato — sin
      // esto queda desactualizado tras guardar acá.
      { query: GET_ALL_DEVICE_ACCESS_FOR_DEVICE, variables: { deviceId } },
    ],
    onCompleted() {
      methods.reset({
        deviceId,
        organizationIds: methods.getValues('organizationIds'),
        updatedRelations: [],
      });
      openModalSuccessMessage(_(msg`Relaciones actualizadas correctamente`));
    },
    onError(error) {
      openModalErrorMessage(error.message);
    },
  });

  const onSubmit = methods.handleSubmit((formData) => {
    updateRelations({
      variables: {
        deviceId,
        inputs: formData.updatedRelations,
      },
    });
  });

  return (
    <form onSubmit={onSubmit}>
      <TabContent
        slotProps={{
          modalActions: {
            onClose,
            onSubmit,
            onResetForm: () => methods.reset(),
            submitLoading: methods.formState.isSubmitting,
            dirtyForm: methods.formState.isDirty,
          },
        }}
      >
        <Typography variant="h6" mb={2}>
          <Trans>Colecciones a las que pertenece el dispositivo</Trans>
        </Typography>
        <Controller
          control={methods.control}
          name="organizationIds"
          render={({ field: { ref, ...orgFieldProps } }) => (
            <Controller
              control={methods.control}
              name="updatedRelations"
              render={({ field: { ref: relationsRef, ...relationsFieldProps } }) => (
                <DeviceCollectionsAccessInput
                  height={380}
                  deviceId={deviceId}
                  organizationIds={orgFieldProps.value}
                  onOrganizationIdsChange={orgFieldProps.onChange}
                  value={relationsFieldProps.value}
                  onChange={relationsFieldProps.onChange}
                />
              )}
            />
          )}
        />
      </TabContent>
    </form>
  );
};
